import type { ReactNode } from 'react';
import type { CatalogRoute } from './useCatalogRoute';
import type { CatalogVertical } from 'src/utils/catalogUrl';

import { Funnel } from '@phosphor-icons/react';
import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { QueryKeys } from 'src/queries';

import { Modal } from 'src/components/Modal';
import { CatalogFilters } from 'src/components/CatalogFilters';
import { ActionButton } from 'src/components/themed/ActionButton';

import { useIsWide } from './useMediaQuery';

/**
 * The filters of one catalog screen, put where the width wants them: a column beside the grid from
 * the `md` breakpoint, a sheet behind the funnel below it. Every change is a new address through the
 * screen's `useCatalogRoute`, so the column and the sheet always show the same thing.
 *
 * `sidebar` goes first in the screen's `CatalogBody`, `funnel` into its header, `sheet` anywhere in
 * the tree (a dialog). A window that grows wide closes the sheet — its column is on screen then — so
 * narrowing it again does not bring back a sheet nobody reopened.
 *
 * `refresh` re-asks the lists and their counts. The screen calls it next to its own feed — from
 * "Refresh the list" and from "Try again" after a failure: the number under the heading and the
 * numbers in the lists are one count, and cars that came back without their lists would leave the
 * person a filter with nothing to choose from. `retryLookup` is the same "Try again" one step
 * earlier — when it was the make or the model of the address that could not be looked up: the
 * lookup is asked again, and the lists with it.
 */
export const useCatalogFilterPanel = (
  vertical: CatalogVertical,
  route: CatalogRoute,
  sellerId?: string
): {
  reset: () => void;
  refresh: () => void;
  retryLookup: () => void;
  sidebar: ReactNode;
  sheet: ReactNode;
  funnel: ReactNode;
} => {
  const wide = useIsWide();
  const [sheetOpen, setSheetOpen] = useState(false);
  const client = useQueryClient();
  const refresh = useCallback(() => {
    client.invalidateQueries({ queryKey: QueryKeys.catalogFacets(vertical) });
  }, [client, vertical]);

  const { retryLookup: retryRouteLookup } = route;
  const retryLookup = useCallback(() => {
    retryRouteLookup();
    refresh();
  }, [retryRouteLookup, refresh]);

  if (wide && sheetOpen) setSheetOpen(false);

  const reset = () => route.navigateTo({ makeSlug: undefined, modelSlug: undefined, filters: {} });
  const panel = (mode: 'sidebar' | 'drawer') => (
    <CatalogFilters
      // One panel per catalog: the lists it keeps belong to this vertical and this dealer, and must
      // not stand in for another's.
      key={`${vertical}:${sellerId ?? ''}`}
      vertical={vertical}
      mode={mode}
      sellerId={sellerId}
      values={{
        makeSlug: route.makeSlug,
        modelSlug: route.modelSlug,
        make: route.make,
        model: route.model,
        filters: route.filters,
      }}
      onApply={(next) => {
        setSheetOpen(false);
        route.navigateTo(next);
      }}
      onReset={() => {
        setSheetOpen(false);
        reset();
      }}
    />
  );

  return {
    reset,
    refresh,
    retryLookup,
    sidebar: wide ? panel('sidebar') : null,
    sheet: (
      <Modal
        visible={sheetOpen}
        onClose={() => setSheetOpen(false)}
        icon={<Funnel size={64} color="white" />}
        label="Filter cars"
      >
        {panel('drawer')}
      </Modal>
    ),
    funnel: wide ? null : (
      <ActionButton onClick={() => setSheetOpen(true)} label="Filter cars">
        <Funnel size={24} color="white" />
      </ActionButton>
    ),
  };
};
